import { AppointmentStatusCodes } from '@lmt/shared-types';
import { EmptyBlock } from '@lmt/unicorn-shared/app/components/patients/profile/overview/components/EmptyBlock';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useState } from 'react';
import { RecentVisitInfo, RecentVisitItem } from './RecentVisitItem';
import { groupBy } from 'lodash';
import { getLongMonthDateWithoutYear } from '@lmt/unicorn-shared/app/components/patients/common';

import styles from './RecentVisitsPanel.module.scss';

export interface RecentVisitsPanelProps {
  recentVisits: RecentVisitInfo[];
  openPatientTab: (patientId: number) => void;
  openCaseVisitTab: (
    patientId: number,
    caseId: number,
    visitId: number,
  ) => void;
}

export const RecentVisitsPanel: React.FC<RecentVisitsPanelProps> = (props) => {
  const { recentVisits, openCaseVisitTab, openPatientTab } = props;
  const i18next = useScopedTranslation('dashboard.recent_visits');
  const [currentTab, setCurrentTab] = useState<'canceled' | 'completed'>(
    'completed',
  );

  const groupedVisits = groupBy(
    recentVisits
      .filter(
        currentTab === 'completed'
          ? (visit) => visit.statusId === AppointmentStatusCodes.Completed
          : (visit) => visit.statusId === AppointmentStatusCodes.Canceled,
      )
      .sort((a, b) => b.startDate!.getTime() - a.startDate!.getTime()),
    (visit) => getLongMonthDateWithoutYear(visit.startDate),
  );

  const visitListComponent = (
    <div className={styles.visits}>
      {Object.keys(groupedVisits).map((createDate) => (
        <div key={createDate} className={styles.dateGroupContainer}>
          <div className={styles.dateTitle}>{createDate}</div>
          <div>
            {groupedVisits[createDate].map((visit) => (
              <div key={visit.id} className={styles.visitContainer}>
                <RecentVisitItem
                  visit={visit}
                  openCaseVisitTab={openCaseVisitTab}
                  openPatientTab={openPatientTab}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{i18next.t('title')}</div>
        <div className={styles.tabs}>
          <div
            className={styles.tab}
            data-selected={currentTab === 'completed'}
            onClick={() => setCurrentTab('completed')}
          >
            {i18next.t('completed')}
          </div>
          <div
            className={styles.tab}
            data-selected={currentTab === 'canceled'}
            onClick={() => setCurrentTab('canceled')}
          >
            {i18next.t('canceled')}
          </div>
        </div>
      </div>
      {Object.keys(groupedVisits).length > 0 ? (
        visitListComponent
      ) : (
        <EmptyBlock title={''} text={i18next.t(`empty_${currentTab}`)} />
      )}
    </div>
  );
};
